import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { selectAccountSettings } from './SignedInAccount'
import { selectGroups } from 'redux/selectors/Groups'
import { arrayToObject } from 'utils/functions'

// Select whether the user has access to export tables
export const selectAllowAccessExportTables = createSelector(
  [selectAccountSettings],
  (accountSettings) => {
    return (
      !!accountSettings.allowAccess.export_tables &&
      !accountSettings.isCompanyGroup &&
      !!(
        accountSettings.isHomepal ||
        accountSettings.allowEarlyAccessExportTables
      )
    )
  }
)

// Select whether the user can manage API keys (admins with a use for them)
export const selectAllowAccessApiKeys = createSelector(
  [selectAccountSettings, selectAllowAccessExportTables],
  (accountSettings, allowAccessExportTables) =>
    accountSettings.isHomepal ||
    (accountSettings.isAdmin &&
      (accountSettings.isManagementCompany || allowAccessExportTables))
)

// Select whether the user has access to analyses (and activities).
export const selectAllowAccessAnalyses = createSelector(
  [
    selectAccountSettings,
    (state: ApplicationState) => state.AuthStore.isAuthenticated
  ],
  (accountSettings, isAuthenticated) => {
    return isAuthenticated && !!accountSettings.allowAccess.allow_analyses
  }
)

/**
 * Select whether the current user is allowed to embed dashboards.
 * Returns true if the user is an admin or Homepal user,
 * and the customer has embed access enabled.
 */
export const selectAllowEmbedDashboard = createSelector(
  [selectAccountSettings],
  (accountSettings): boolean => {
    return accountSettings.isAdmin && accountSettings.allowAccess.embed
  }
)

/**
 * Select whether the current user can edit a dashboard group.
 * Returns true if the user is an admin, a Homepal user,
 * or the owner of the dashboard group.
 *
 * @param state - Application state
 * @param dashboardGroupId - Dashboard group ID
 * @returns boolean
 */
export const selectAllowEditDashboardGroup = createSelector(
  [
    // Input selectors:
    selectAccountSettings,
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (_: ApplicationState, dashboardGroupId: string | null | undefined) =>
      dashboardGroupId
  ],
  (accountSettings, dashboardGroups, dashboardGroupId): boolean => {
    if (accountSettings.isAdmin) {
      return true
    }

    if (!dashboardGroupId) return false

    const dashboardGroup = dashboardGroups[dashboardGroupId]

    if (!dashboardGroup) return false

    return dashboardGroup.owner === accountSettings.account?.id
  }
)

/**
 * Select whether the current user can edit a dashboard.
 * Returns true if the user is an admin, a Homepal user,
 * or the owner of the dashboard's group.
 *
 * @param state - Application state
 * @param dashboardId - Dashboard ID
 * @returns boolean
 */
export const selectCanEditDashboard = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (_: ApplicationState, dashboardId: string | null | undefined) =>
      dashboardId,
    (state: ApplicationState) => state
  ],
  (dashboards, dashboardId, state): boolean => {
    if (!dashboardId) return false

    const dashboard = dashboards[dashboardId]

    if (!dashboard) return false

    return selectAllowEditDashboardGroup(state, dashboard.dashboard_group_id)
  }
)

export const selectAllowAccessMetric = createSelector(
  [
    (state: ApplicationState) => state.MetricStore.data,
    selectAccountSettings,
    selectGroups,
    (_: ApplicationState, metricId: string | null | undefined) => metricId
  ],
  (metrics, accountSettings, { myGroups }, metricId): boolean => {
    if (!metricId) return false

    const metric = metrics[metricId]

    if (!metric) return false

    const userGroups = arrayToObject(myGroups)

    const validatePrivateMetrics =
      metric.access.private &&
      !accountSettings.isAdmin &&
      !accountSettings.isHomepal

    if (validatePrivateMetrics) {
      return metric.access.permissions.some((perm) => {
        if (perm.account_id)
          return perm.account_id === accountSettings.account?.id
        if (perm.group_id) return !!userGroups[perm.group_id]

        return false
      })
    }

    return true
  }
)
